import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { CodingModel, MemberAppointmentModel, MemberAppointmentStatus, MemberAppointmentsService, codingOf } from '@lib/guarantees/data-access';
import { selectTimelineVisits } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, distinctUntilChanged, of, switchMap, tap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { OptionListComponent } from '../components/common/option-list.component';
import { OptionRowDirective } from '../components/common/option-row.directive';
import { OVERLAY_POSITIONS, SearchFieldComponent, fieldWidth } from '../components/common/search-field.component';

/** A visit as the guarantee names it, with anything already known about what was diagnosed there. */
export interface AppointmentChoice {
  id: string;
  diagnoses: CodingModel[];
  /** The booking itself, for a flow that records where and when rather than only which visit. */
  appointment: MemberAppointmentModel;
}

export const APPOINTMENT_STATUS_LABEL_KEY: Record<MemberAppointmentStatus, string> = {
  Created: 'guaranteeV2.label.appointmentCreated',
  Completed: 'guaranteeV2.label.appointmentCompleted',
  Cancelled: 'guaranteeV2.label.appointmentCancelled',
  NoShow: 'guaranteeV2.label.appointmentNoShow',
};

/**
 * Picks the visit a guarantee answers.
 *
 * Fetched once per member and narrowed HERE: the appointments search takes no free-text term
 * (`ServiceId` is its only service filter). A guarantee already on the appointment brings its
 * diagnoses with the choice.
 */
@Component({
  selector: 'guarantees-appointment-picker',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, DatePipe, MatIcon, MatTooltip, TranslatePipe, OptionListComponent, OptionRowDirective, SearchFieldComponent],
  templateUrl: './appointment-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentPickerComponent {
  memberId = input.required<string>();
  /** The appointment already named, so re-entering the step shows what was chosen. */
  selectedId = input<string>();
  /** Only visits in these states are offered. Unset offers every visit, whatever became of it. */
  allowedStatuses = input<MemberAppointmentStatus[] | undefined>(undefined);

  chosen = output<AppointmentChoice>();
  /** The visit named up front is not one that may be taken, so whoever named it can let it go. */
  selectedUnavailable = output<void>();

  private readonly appointments = inject(MemberAppointmentsService);
  private readonly store = inject(Store);
  private readonly visits = this.store.selectSignal(selectTimelineVisits);

  protected readonly statuses = MemberAppointmentStatus;
  protected readonly statusLabelKey = APPOINTMENT_STATUS_LABEL_KEY;

  protected readonly overlayPositions = OVERLAY_POSITIONS;

  private readonly origin = viewChild.required(CdkOverlayOrigin);

  protected readonly panelWidth = signal(0);

  /** Every way the list opens comes through here, so it is never shown before it is sized. */
  protected unfold(): void {
    this.panelWidth.set(fieldWidth(this.origin()));
    this.open.set(true);
  }

  protected readonly term = signal('');
  protected readonly open = signal(false);
  protected readonly loading = signal(false);
  protected readonly failed = signal(false);

  /** Everything fetched for this member — the window the typing narrows. */
  private readonly fetched = signal<MemberAppointmentModel[]>([]);

  /** What may be offered at all: a visit in a state the caller does not accept is never listed. */
  private readonly all = computed(() => {
    const allowed = this.allowedStatuses();

    return allowed ? this.fetched().filter(appointment => allowed.includes(appointment.status)) : this.fetched();
  });

  /** Which appointments already carry a guarantee, so the list can say so before one is picked. */
  private readonly guaranteedAppointmentIds = computed(
    () =>
      new Set(
        this.visits()
          .map(visit => visit.appointmentId)
          .filter((id): id is string => !!id)
      )
  );

  protected hasGuarantee(appointmentId: string): boolean {
    return this.guaranteedAppointmentIds().has(appointmentId);
  }

  /** What the field shows once a visit is taken, so the choice survives leaving the step. */
  protected readonly display = computed(() => {
    const chosen = this.all().find(appointment => appointment.id === this.selectedId());

    return chosen ? this.labelOf(chosen) : '';
  });

  /** Newest first throughout: the visit a guarantee answers is nearly always a recent one, and the
   * status is on the row rather than in the order, so a cancelled booking is recognised without
   * being buried under older ones. */
  protected readonly results = computed(() => {
    const term = this.term().trim().toLowerCase();
    const matched = term ? this.all().filter(appointment => this.haystack(appointment).includes(term)) : this.all();

    return [...matched].sort((a, b) => (b.dateStart ?? '').localeCompare(a.dateStart ?? ''));
  });

  private haystack(appointment: MemberAppointmentModel): string {
    return [appointment.serviceName, appointment.doctorName, appointment.partnerName, appointment.locationName].filter(Boolean).join(' ').toLowerCase();
  }

  private labelOf(appointment: MemberAppointmentModel): string {
    return [appointment.serviceName, appointment.doctorName].filter(Boolean).join(' · ') || appointment.id;
  }

  constructor() {
    // Read through `toObservable`, never in the constructor body: a required input has no value
    // until Angular binds it, and reading it before then throws.
    toObservable(this.memberId)
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
        }),
        switchMap(memberId =>
          memberId
            ? this.appointments.search(memberId).pipe(
                catchError(() => {
                  this.failed.set(true);

                  return of<MemberAppointmentModel[]>([]);
                })
              )
            : of<MemberAppointmentModel[]>([])
        ),
        takeUntilDestroyed()
      )
      .subscribe(items => {
        this.fetched.set(items);
        this.loading.set(false);

        const selectedId = this.selectedId();
        if (selectedId && !this.all().some(appointment => appointment.id === selectedId)) {
          this.selectedUnavailable.emit();
        }
      });
  }

  protected search(value: string): void {
    this.term.set(value);
    this.unfold();
  }

  protected choose(appointment: MemberAppointmentModel): void {
    this.term.set('');
    this.open.set(false);
    this.chosen.emit({
      id: appointment.id,
      diagnoses: this.diagnosesOf(appointment.id),
      appointment,
    });
  }

  /** Codes already stated against this visit by a guarantee raised on it before. */
  private diagnosesOf(appointmentId: string): CodingModel[] {
    const byCode = new Map<string, CodingModel>();
    for (const visit of this.visits()) {
      if (visit.appointmentId !== appointmentId) {
        continue;
      }

      for (const diagnosis of visit.diagnoses) {
        const coding = codingOf(diagnosis);
        if (coding?.code && !byCode.has(coding.code)) {
          byCode.set(coding.code, coding);
        }
      }
    }

    return [...byCode.values()];
  }
}
